import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Edit, Trash2, X, Save, Clock, User, Loader2 } from 'lucide-react';
import { useObservations } from '../../hooks/useObservations';
import UserProvider from '../../infra/provider/userProvider';
import '../../styles/components/_observations.scss';

export interface Observation {
    id: string;
    text: string;
    responsible: string;
    createdAt: string;
    updatedAt?: string;
}

interface ObservationsPanelProps {
    entityId: string;
    entityType: 'enterprise' | 'incorporadora';
    onObservationChange?: (observations: Observation[]) => void;
    initialObservations?: Observation[];
}

const ObservationsPanel: React.FC<ObservationsPanelProps> = ({
    entityId,
    entityType,
    onObservationChange,
    initialObservations = []
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [newObservation, setNewObservation] = useState('');
    const [editingText, setEditingText] = useState('');
    const [deletingObservations, setDeletingObservations] = useState<Set<string>>(new Set());
    const [currentUser, setCurrentUser] = useState<{ name: string; id: string }>({
        name: 'Usuário Atual',
        id: '1'
    });

    const {
        observations,
        isLoading,
        error,
        addObservation,
        editObservation,
        deleteObservation
    } = useObservations({
        entityId,
        entityType,
        onObservationsChange: onObservationChange,
        initialObservations
    });

    // Carregar informações do usuário atual
    useEffect(() => {
        const loadCurrentUser = async () => {
            try {
                const userProvider = UserProvider.getInstance();
                const user = await userProvider.getCurrentUser();
                setCurrentUser({
                    name: user.name,
                    id: user.id
                });
            } catch (error) {
                console.error('Erro ao carregar usuário atual:', error);
                // Manter o valor padrão em caso de erro
            }
        };

        loadCurrentUser();
    }, []);

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleAddObservation = async () => {
        if (!newObservation.trim()) return;

        try {
            await addObservation(newObservation.trim(), currentUser.name);
            setNewObservation('');
            setIsAdding(false);
        } catch (error) {
            console.error('Erro ao adicionar anotação:', error);
        }
    };

    const handleEditObservation = (id: string) => {
        const observation = observations.find(obs => obs.id === id);
        if (observation) {
            setEditingText(observation.text);
            setEditingId(id);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingText.trim() || !editingId) return;

        try {
            await editObservation(editingId, editingText.trim());
            setEditingId(null);
            setEditingText('');
        } catch (error) {
            console.error('Erro ao editar anotação:', error);
        }
    };

    const handleDeleteObservation = async (id: string) => {
        // Adiciona à lista de observações sendo deletadas
        setDeletingObservations(prev => new Set(prev).add(id));

        // Aguarda a animação terminar (600ms) e então executa a exclusão
        setTimeout(async () => {
            try {
                await deleteObservation(id);
                setDeletingObservations(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(id);
                    return newSet;
                });
            } catch (error) {
                console.error('Erro ao excluir anotação:', error);
                // Remove da lista de deletando em caso de erro
                setDeletingObservations(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(id);
                    return newSet;
                });
            }
        }, 600);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingText('');
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setNewObservation('');
    };

    return (
        <div className={`observations-panel ${isExpanded ? 'expanded' : ''}`}>
            {/* Aba lateral */}
            <button
                className="observations-tab"
                onClick={() => setIsExpanded(!isExpanded)}
                title="Anotações"
            >
                <div className="tab-icon">
                    <MessageSquare size={20} />
                    {observations.length > 0 && (
                        <span className="tab-count">{observations.length}</span>
                    )}
                </div>
            </button>

            {/* Drawer de observações */}
            <div className="observations-drawer">
                <div className="observations-header">
                    <h3>
                        <MessageSquare size={18} />
                        Anotações
                    </h3>
                    <button
                        className="close-btn"
                        onClick={() => setIsExpanded(false)}
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="observations-body">
                    {/* Estado de carregamento */}
                    {isLoading && (
                        <div className="loading-state">
                            <Loader2 size={24} className="spinner" />
                            <p>Carregando observações...</p>
                        </div>
                    )}

                    {/* Estado de erro */}
                    {error && (
                        <div className="error-state">
                            <p>Erro: {error}</p>
                        </div>
                    )}

                    {/* Botão para adicionar nova anotação */}
                    {!isLoading && !error && !isAdding && (
                        <button
                            className="add-observation-btn"
                            onClick={() => setIsAdding(true)}
                        >
                            <Plus size={16} />
                            Nova anotação
                        </button>
                    )}

                    {/* Formulário para adicionar anotação */}
                    {isAdding && (
                        <div className="observation-form">
                            <textarea
                                value={newObservation}
                                onChange={(e) => setNewObservation(e.target.value)}
                                placeholder="Digite sua anotação..."
                                rows={3}
                                autoFocus
                            />
                            <div className="form-actions">
                                <button
                                    className="save-btn"
                                    onClick={handleAddObservation}
                                    disabled={!newObservation.trim()}
                                >
                                    <Save size={14} />
                                    Salvar
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={handleCancelAdd}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Lista de observações */}
                    {!isLoading && !error && (
                        <div className="observations-list">
                            {observations.length === 0 ? (
                                <div className="empty-state">
                                    <MessageSquare size={48} />
                                    <p>Nenhuma anotação registrada</p>
                                </div>
                            ) : (
                                observations.map((observation) => (
                                    <div
                                        key={observation.id}
                                        className={`observation-item ${deletingObservations.has(observation.id) ? 'deleting' : ''}`}
                                    >
                                        {editingId === observation.id ? (
                                            <div className="observation-edit">
                                                <textarea
                                                    value={editingText}
                                                    onChange={(e) => setEditingText(e.target.value)}
                                                    rows={3}
                                                    autoFocus
                                                />
                                                <div className="edit-actions">
                                                    <button
                                                        className="save-btn"
                                                        onClick={handleSaveEdit}
                                                        disabled={!editingText.trim()}
                                                    >
                                                        <Save size={14} />
                                                        Salvar
                                                    </button>
                                                    <button
                                                        className="cancel-btn"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="observation-header">
                                                    <div className="observation-meta">
                                                        <div className="responsible">
                                                            <User size={14} />
                                                            <span>{observation.responsible}</span>
                                                        </div>
                                                        <div className="datetime">
                                                            <Clock size={14} />
                                                            <span>
                                                                {formatDateTime(observation.createdAt)}
                                                                {observation.updatedAt && (
                                                                    <span className="edited">
                                                                        (editado em {formatDateTime(observation.updatedAt)})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="observation-actions">
                                                        <button
                                                            className="edit-btn"
                                                            onClick={() => handleEditObservation(observation.id)}
                                                            title="Editar"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            className="delete-btn"
                                                            onClick={() => handleDeleteObservation(observation.id)}
                                                            title="Excluir"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="observation-text">
                                                    {observation.text}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>


        </div>
    );
};

export default ObservationsPanel;
